import { useOption } from "@mui/base";
import { Option as BaseOption } from "@mui/base/Option";
import {
  Select as BaseSelect,
  SelectProps,
  SelectRootSlotProps,
} from "@mui/base/Select";
import clsx from "clsx";
import * as React from "react";

import { Icon } from "../../../assets/icons/Icon";
import styles from "./Select.module.scss";

interface OptionProps {
  children?: React.ReactNode;
  className?: string;
  value: string;
  disabled?: boolean;
  search?: boolean;
}

const Select = React.forwardRef(function Select<
  TValue extends {},
  Multiple extends boolean,
>(
  props: SelectProps<TValue, Multiple>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const slots: SelectProps<TValue, Multiple>["slots"] = {
    root: CustomButton,
    popup: "div",
    ...props.slots,
  };
  const listboxObj =
    (props.slotProps?.listbox as { className?: string } | undefined) ?? {};

  return (
    <BaseSelect
      {...props}
      ref={ref}
      slots={slots}
      className={clsx(props.className)}
      slotProps={{
        ...props.slotProps,
        listbox: {
          ...listboxObj,
          className: clsx(
            styles.selectList, // base class
            listboxObj.className, // user class if any
          ),
        },
      }}
    />
  );
}) as <TValue extends {}, Multiple extends boolean>(
  props: SelectProps<TValue, Multiple> & React.RefAttributes<HTMLButtonElement>,
) => React.JSX.Element;

function Option(props: OptionProps) {
  const { children, value, className, disabled = false } = props;
  const { getRootProps, highlighted } = useOption({
    value,
    disabled,
    label: children,
  });

  return (
    <BaseOption
      {...getRootProps()}
      value={value}
      className={clsx({ highlighted }, className)}
    >
      {children}
    </BaseOption>
  );
}

const CustomButton = React.forwardRef(function CustomButton<
  TValue extends {},
  Multiple extends boolean,
>(
  props: SelectRootSlotProps<TValue, Multiple>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const {
    className,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ownerState: _,
    children,
    "aria-expanded": expanded,
    ...other
  } = props;

  return (
    <button
      type="button"
      {...other}
      ref={ref}
      className={clsx(styles.button, className)}
    >
      {children}
      {expanded ? (
        <Icon iconName="IconUp" width="1rem" />
      ) : (
        <Icon iconName="IconDown" width="1rem" />
      )}
    </button>
  );
});

export { Select, Option };
